import { User, Briefcase } from 'lucide-react';
import type { Analysis } from '../../types/models';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface AnalysisSummaryProps {
  analysis: Analysis;
}

export function AnalysisSummary({ analysis }: AnalysisSummaryProps) {
  const [candidateDetails, setCandidateDetails] = useState({ name: '', cv_text: '' });
  const [jobDetails, setJobDetails] = useState({ title: '', company: '', jd_text: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchDetails() {
      setIsLoading(true);
      try {
        // Fetch candidate details
        const { data: candidateData } = await supabase
          .from('candidates')
          .select('name, cv_text')
          .eq('id', analysis.candidate_id)
          .maybeSingle();

        // Fetch job details
        const { data: jobData } = await supabase
          .from('jobs')
          .select('title, company, jd_text')
          .eq('id', analysis.job_id)
          .maybeSingle();

        if (candidateData) {
          setCandidateDetails(candidateData);
        }
        if (jobData) {
          setJobDetails(jobData);
        }
      } catch (error) {
        console.error("Error fetching summary details:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchDetails();
  }, [analysis.candidate_id, analysis.job_id]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center space-x-2 pb-3">
          <User className="h-5 w-5 text-primary" />
          <CardTitle className="text-base font-semibold">Candidate Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-medium text-muted-foreground">Name</p>
            {isLoading ? <Skeleton className="h-5 w-3/4 mt-1" /> : 
               <p className="text-foreground">{candidateDetails.name || '-'}</p>}
          </div>
          <div>
            <p className="font-medium text-muted-foreground">CV Summary</p>
            {isLoading ? <Skeleton className="h-12 w-full mt-1" /> : 
               <p className="text-muted-foreground whitespace-pre-wrap break-words">{analysis.cv_summary || 'Not provided'}</p>}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center space-x-2 pb-3">
          <Briefcase className="h-5 w-5 text-primary" />
          <CardTitle className="text-base font-semibold">Position Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-medium text-muted-foreground">Position</p>
            {isLoading ? <Skeleton className="h-5 w-3/4 mt-1" /> : 
               <p className="text-foreground">{jobDetails.title || '-'}</p>}
          </div>
          {!isLoading && jobDetails.company && (
            <div>
              <p className="font-medium text-muted-foreground">Company</p>
              <p className="text-foreground">{jobDetails.company}</p>
            </div>
          )}
          <div>
            <p className="font-medium text-muted-foreground">Requirements</p>
            {isLoading ? <Skeleton className="h-12 w-full mt-1" /> : 
               <p className="text-muted-foreground whitespace-pre-wrap break-words">{analysis.job_requirements || 'Not provided'}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}